var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { createSlice } from '@reduxjs/toolkit';
import { initialProgressState } from '../progress/ProgressManager';
var registerReducers = require('@cdo/apps/redux').registerReducers;
/**
 * State, reducer, and actions for Music Lab.
 */
var InstructionsPosition;
(function (InstructionsPosition) {
    InstructionsPosition["TOP"] = "TOP";
    InstructionsPosition["LEFT"] = "LEFT";
    InstructionsPosition["RIGHT"] = "RIGHT";
})(InstructionsPosition || (InstructionsPosition = {}));
// Exporting enum as object for use in JS files
export var InstructionsPositions = {
    TOP: InstructionsPosition.TOP,
    LEFT: InstructionsPosition.LEFT,
    RIGHT: InstructionsPosition.RIGHT,
};
var initialState = {
    isPlaying: false,
    currentPlayheadPosition: 0,
    selectedBlockId: undefined,
    timelineAtTop: false,
    showInstructions: false,
    instructionsPosition: InstructionsPosition.LEFT,
    isBeatPadShowing: false,
    playbackEvents: [],
    lastMeasure: 0,
    levelCount: undefined,
    currentProgressState: __assign({}, initialProgressState),
};
var musicSlice = createSlice({
    name: 'music',
    initialState: initialState,
    reducers: {
        setIsPlaying: function (state, action) {
            state.isPlaying = action.payload;
        },
        setCurrentPlayheadPosition: function (state, action) {
            state.currentPlayheadPosition = action.payload;
        },
        // If the user selects a block ID by clicking a timeline element, then
        // set the selected block ID.
        // If the user selects the currently-selected block ID, then unselect it.
        // If undefined is provided, we'll unselect all blocks.
        // During playback, we are dynamically highlighting blocks which overrides
        // the selection, so just do nothing here.
        selectBlockId: function (state, action) {
            if (state.isPlaying) {
                return;
            }
            if (state.selectedBlockId === action.payload) {
                state.selectedBlockId = undefined;
            }
            else {
                state.selectedBlockId = action.payload;
            }
        },
        clearSelectedBlockId: function (state) {
            state.selectedBlockId = undefined;
        },
        toggleTimelinePosition: function (state) {
            state.timelineAtTop = !state.timelineAtTop;
        },
        setShowInstructions: function (state, action) {
            state.showInstructions = action.payload;
        },
        toggleInstructions: function (state) {
            state.showInstructions = !state.showInstructions;
        },
        setInstructionsPosition: function (state, action) {
            state.instructionsPosition = action.payload;
        },
        advanceInstructionsPosition: function (state) {
            var positions = Object.values(InstructionsPosition);
            state.instructionsPosition =
                positions[(positions.indexOf(state.instructionsPosition) + 1) % positions.length];
        },
        showBeatPad: function (state) {
            state.isBeatPadShowing = true;
        },
        hideBeatPad: function (state) {
            state.isBeatPadShowing = false;
        },
        toggleBeatPad: function (state) {
            state.isBeatPadShowing = !state.isBeatPadShowing;
        },
        setCurrentProgressState: function (state, action) {
            state.currentProgressState = __assign({}, action.payload);
        },
        clearPlaybackEvents: function (state) {
            state.playbackEvents = [];
            state.lastMeasure = 0;
        },
        addPlaybackEvents: function (state, action) {
            var _a;
            (_a = state.playbackEvents).push.apply(_a, action.payload.events);
            state.lastMeasure = action.payload.lastMeasure;
        },
        setLevelCount: function (state, action) {
            state.levelCount = action.payload;
        },
    },
});
// Selectors
export var getCurrentlyPlayingBlockIds = function (state) {
    var _a = state.music, currentPlayheadPosition = _a.currentPlayheadPosition, playbackEvents = _a.playbackEvents;
    var playingBlockIds = [];
    playbackEvents.forEach(function (playbackEvent) {
        var currentlyPlaying = currentPlayheadPosition !== 0 &&
            currentPlayheadPosition >= playbackEvent.when &&
            currentPlayheadPosition < playbackEvent.when + playbackEvent.length;
        if (currentlyPlaying) {
            playingBlockIds.push(playbackEvent.blockId);
        }
    });
    return playingBlockIds;
};
// TODO: If/when a top-level component is created that wraps {@link MusicView}, then
// registering reducers should happen there. We are registering reducers here for now
// because MusicView is currently the top-level entrypoint into Music Lab and also needs
// to be connected to this state.
registerReducers({ music: musicSlice.reducer });
export var setIsPlaying = (_a = musicSlice.actions, _a.setIsPlaying), setCurrentPlayheadPosition = _a.setCurrentPlayheadPosition, selectBlockId = _a.selectBlockId, clearSelectedBlockId = _a.clearSelectedBlockId, toggleTimelinePosition = _a.toggleTimelinePosition, setShowInstructions = _a.setShowInstructions, setInstructionsPosition = _a.setInstructionsPosition, toggleInstructions = _a.toggleInstructions, advanceInstructionsPosition = _a.advanceInstructionsPosition, showBeatPad = _a.showBeatPad, hideBeatPad = _a.hideBeatPad, toggleBeatPad = _a.toggleBeatPad, setCurrentProgressState = _a.setCurrentProgressState, clearPlaybackEvents = _a.clearPlaybackEvents, addPlaybackEvents = _a.addPlaybackEvents, setLevelCount = _a.setLevelCount;
